/**
 * The HUD — the chrome around the editor that is not the editor.
 *
 * Owned by the auth/billing lane: identity, plan, save state, quota. The tool
 * rail, canvas, layers and properties panels belong to the shell
 * (`src/app/**`). The split exists so two forks can work without fighting over
 * one file; see docs/decisions/auth-shell-contract.md.
 *
 * Everything here is plain DOM with class names from `public/hud.css`. No
 * framework, no inline colours, and no token reached past the component layer.
 */

export type { AccountMenu, AccountMenuOptions } from "./account-menu";
export { createAccountMenu } from "./account-menu";
export type { BillingPanel } from "./billing-panel";
export { createBillingPanel } from "./billing-panel";
export { actionButton, el, formatDate, formatRelative, once, replace } from "./dom";
export type { LocalStoreUnavailable, SaveLocalInput } from "./local-store";
export {
  deleteLocalProject,
  listLocalProjects,
  localBytesUsed,
  markMigrated,
  readLocalProject,
  saveLocalProject,
} from "./local-store";
export type { MigrationNotice } from "./migration-notice";
export { createMigrationNotice, migrationNotice } from "./migration-notice";
export type { SaveController, SaveDeps, Snapshot } from "./save-controller";
export { cloudFailure, createSaveController } from "./save-controller";
export type { SaveIndicator, SaveState } from "./save-state";
export { createSaveIndicator, describeSaveState } from "./save-state";
export type { NoticeTone, SubscriptionNotice } from "./subscription-notice";
export { readOnlyNotice, subscriptionNotice } from "./subscription-notice";
export type { UpgradePanel, UpgradePanelOptions } from "./upgrade-panel";
export { createUpgradePanel, quotaPrompt } from "./upgrade-panel";

import { type AccountMenu, type AccountMenuOptions, createAccountMenu } from "./account-menu";
import { createSaveIndicator, type SaveIndicator } from "./save-state";

export interface AppBarSlots {
  /** Where the save state goes — beside the project name, on the left. */
  save?: HTMLElement | null;
  /** Where the account control goes — the right end of the bar. */
  account?: HTMLElement | null;
}

export interface AppBarHud {
  account: AccountMenu | null;
  save: SaveIndicator | null;
  destroy(): void;
}

/**
 * Mount the HUD pieces that live in the app bar.
 *
 * Two slots, not one. The save state belongs beside the project name and the
 * account control belongs at the far end — they are in different halves of the
 * bar, and an earlier version of this took a single element and appended both,
 * which would have put the save state inside the account cluster. Wiring it is
 * what surfaced that; nothing about the components themselves was wrong.
 *
 * Either slot may be absent. A page that has a bar but no account control is a
 * real configuration, and a missing slot should mean "not here" rather than a
 * crash.
 */
export function mountAppBarHud(slots: AppBarSlots, options: AccountMenuOptions = {}): AppBarHud {
  const save = slots.save ? createSaveIndicator() : null;
  if (save && slots.save) slots.save.appendChild(save.el);

  const account = slots.account ? createAccountMenu(options) : null;
  if (account && slots.account) slots.account.appendChild(account.el);

  return {
    account,
    save,
    destroy() {
      save?.destroy();
      account?.destroy();
    },
  };
}
